import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  collection,
  deleteDoc,
  doc,
  getFirestore,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import {
  getDownloadURL,
  getStorage,
  deleteObject,
  listAll,
  ref as storageRef,
  uploadBytes,
  uploadString,
} from "firebase/storage";
import { FileAudio, FileText, RefreshCw, Sparkles, Trash2 } from "lucide-react";
import { runPodcastPipeline } from "../../../helpers/AI_functions";

const BUCKET_URI = "gs://wui-go-97d87.appspot.com";
const FILE_STEM = "script";
const STEPS = ["generate", "polish", "audio"];

const STEP_OPTIONS = [
  { id: "generate", label: "Outline", description: "Generate a new outline from the current English script." },
  { id: "polish", label: "Polish", description: "Create a polished full transcript from the generated or existing outline." },
  { id: "audio", label: "Audio generation", description: "Generate MP3 and WAV audio from the polished or existing transcript." },
];

const cardStyle = {
  background: "#fff",
  border: "1px solid #e5e7eb",
  borderRadius: 10,
  padding: 20,
};

const statusColors = {
  complete: { bg: "#dcfce7", fg: "#166534" },
  failed: { bg: "#fee2e2", fg: "#b91c1c" },
  submitted: { bg: "#dbeafe", fg: "#1d4ed8" },
  submitting: { bg: "#fef3c7", fg: "#92400e" },
};

const formatDate = (value) => {
  const date = value?.toDate ? value.toDate() : null;
  return date ? date.toLocaleString() : "";
};

const probeUrl = async (storage, objectPath) => {
  if (!objectPath) return "";
  return getDownloadURL(storageRef(storage, objectPath)).catch(() => "");
};

const deleteStorageFolder = async (folderRef) => {
  const contents = await listAll(folderRef);
  await Promise.all(contents.items.map((itemRef) => deleteObject(itemRef)));
  await Promise.all(contents.prefixes.map((prefixRef) => deleteStorageFolder(prefixRef)));
};

function OutputLink({ label, url, icon }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "10px 12px", border: "1px solid #e5e7eb", borderRadius: 8 }}>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 8, color: "#0f172a", fontWeight: 700 }}>{icon}{label}</span>
      {url ? (
        <a href={url} target="_blank" rel="noreferrer" style={{ color: "#1d4ed8", fontWeight: 700, textDecoration: "none" }}>View / download</a>
      ) : (
        <span style={{ color: "#94a3b8", fontSize: 13 }}>Not available</span>
      )}
    </div>
  );
}

export default function GeneratedAudioSection({ podcastId, nodeId, language, script }) {
  const db = useMemo(() => getFirestore(), []);
  const storage = useMemo(() => getStorage(), []);
  const nodeIdStr = nodeId != null ? String(nodeId) : "";
  const langCode = String(language || "EN").toUpperCase();
  const [runs, setRuns] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [refreshingId, setRefreshingId] = useState("");
  const [deletingId, setDeletingId] = useState("");
  const [regeneratingId, setRegeneratingId] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedSteps, setSelectedSteps] = useState(STEPS);
  const [regenerationRun, setRegenerationRun] = useState(null);

  const runsCollectionRef = useMemo(() => {
    if (!podcastId || !nodeIdStr || langCode !== "EN") return null;
    return collection(
      db,
      "podcasts",
      String(podcastId),
      "audio_generation",
      nodeIdStr,
      langCode
    );
  }, [db, podcastId, nodeIdStr, langCode]);

  const resolveRunUrls = useCallback(async (run) => {
    const [outlineUrl, transcriptUrl, mp3Url, wavUrl] = await Promise.all([
      probeUrl(storage, run.outlinePath),
      probeUrl(storage, run.transcriptPath),
      probeUrl(storage, run.mp3Path),
      probeUrl(storage, run.wavPath),
    ]);
    return { ...run, outlineUrl, transcriptUrl, mp3Url, wavUrl };
  }, [storage]);

  useEffect(() => {
    if (!runsCollectionRef) {
      setRuns([]);
      return undefined;
    }
    const unsubscribe = onSnapshot(
      query(runsCollectionRef, orderBy("createdAt", "desc")),
      async (snapshot) => {
        const rows = await Promise.all(snapshot.docs.map((item) => resolveRunUrls({ id: item.id, ...(item.data() || {}) })));
        setRuns(rows);
      },
      (err) => setError(String(err?.message || "Could not load generated-audio runs."))
    );
    return unsubscribe;
  }, [runsCollectionRef, resolveRunUrls]);

  if (langCode !== "EN") return null;

  const refreshRun = async (run) => {
    if (!runsCollectionRef || !run?.id) return;
    setRefreshingId(run.id);
    setError("");
    try {
      const resolved = await resolveRunUrls(run);
      const availability = {
        outlineAvailable: !!resolved.outlineUrl,
        transcriptAvailable: !!resolved.transcriptUrl,
        mp3Available: !!resolved.mp3Url,
        wavAvailable: !!resolved.wavUrl,
      };
      const expectedSteps = Array.isArray(run.steps) && run.steps.length ? run.steps : STEPS;
      const complete = expectedSteps.every((step) => {
        if (step === "generate") return availability.outlineAvailable;
        if (step === "polish") return availability.transcriptAvailable;
        if (step === "audio") return availability.mp3Available || availability.wavAvailable;
        return true;
      });
      await setDoc(doc(runsCollectionRef, run.id), {
        ...availability,
        ...(complete ? { status: "complete" } : {}),
        updatedAt: serverTimestamp(),
      }, { merge: true });
      setRuns((prev) => prev.map((row) => row.id === run.id ? resolved : row));
      setSuccess(complete ? "All generated outputs are available." : "Output availability refreshed.");
    } catch (err) {
      setError(String(err?.message || "Could not refresh generated outputs."));
    } finally {
      setRefreshingId("");
    }
  };

  const generateAudio = async () => {
    if (!runsCollectionRef || !podcastId || !nodeIdStr || !String(script || "").trim()) return;
    const steps = STEPS.filter((step) => selectedSteps.includes(step));
    if (!steps.length) {
      setError("Select at least one generation step.");
      return;
    }

    const needsExistingOutline = steps.includes("polish") && !steps.includes("generate");
    const needsExistingTranscript = steps.includes("audio") && !steps.includes("polish");
    const reusableRun = needsExistingOutline || needsExistingTranscript
      ? runs.find((run) => {
        if (needsExistingOutline && !run.outlineUrl) return false;
        if (needsExistingTranscript && !run.transcriptUrl) return false;
        return !!run.outputFolder;
      })
      : null;
    if ((needsExistingOutline || needsExistingTranscript) && !reusableRun) {
      setError(needsExistingOutline
        ? "This selection needs an existing outline. Generate an outline first."
        : "This selection needs an existing full transcript. Run Polish first.");
      return;
    }

    setIsGenerating(true);
    setIsDialogOpen(false);
    setError("");
    setSuccess("");

    const runRef = doc(runsCollectionRef);
    const runId = runRef.id;
    const baseObjectPath = `Podcasts/${String(podcastId)}/${langCode}/${nodeIdStr}/audio-generated`;
    const inputObjectPath = `${baseObjectPath}/inputs/${runId}/${FILE_STEM}.txt`;
    const outputObjectFolder = `${baseObjectPath}/runs/${runId}`;
    const statusFirestoreLocation = `podcasts/${String(podcastId)}/audio_generation/${nodeIdStr}/${langCode}/${runId}`;
    const payload = {
      input_location: `${BUCKET_URI}/${inputObjectPath}`,
      steps,
      output_folder: `${BUCKET_URI}/${outputObjectFolder}`,
      status_firestore_location: statusFirestoreLocation,
    };
    const runData = {
      runId,
      podcastId: String(podcastId),
      nodeId: nodeIdStr,
      language: langCode,
      status: "submitting",
      steps,
      inputLocation: payload.input_location,
      inputObjectPath,
      outputFolder: payload.output_folder,
      statusFirestoreLocation,
      outlinePath: `${outputObjectFolder}/${FILE_STEM}_outline.txt`,
      transcriptPath: `${outputObjectFolder}/${FILE_STEM}_full_transcript.txt`,
      mp3Path: `${outputObjectFolder}/${FILE_STEM}_audio.mp3`,
      wavPath: `${outputObjectFolder}/${FILE_STEM}_audio.wav`,
      outlineAvailable: false,
      transcriptAvailable: false,
      mp3Available: false,
      wavAvailable: false,
      error: "",
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    try {
      await uploadString(storageRef(storage, inputObjectPath), String(script), "raw", { contentType: "text/plain; charset=utf-8" });
      if (needsExistingOutline) {
        const outlineResponse = await fetch(reusableRun.outlineUrl);
        if (!outlineResponse.ok) throw new Error("Could not load the existing outline.");
        await uploadBytes(storageRef(storage, runData.outlinePath), await outlineResponse.blob());
      }
      if (needsExistingTranscript) {
        const transcriptResponse = await fetch(reusableRun.transcriptUrl);
        if (!transcriptResponse.ok) throw new Error("Could not load the existing full transcript.");
        await uploadBytes(storageRef(storage, runData.transcriptPath), await transcriptResponse.blob());
      }
      await setDoc(runRef, runData);
      const result = await runPodcastPipeline({
        inputLocation: payload.input_location,
        steps: payload.steps,
        outputFolder: payload.output_folder,
        statusFirestoreLocation: payload.status_firestore_location,
      });
      await setDoc(runRef, {
        status: "submitted",
        apiResponse: result || {},
        submittedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      }, { merge: true });
      setSuccess("Audio-generation request submitted.");
    } catch (err) {
      const message = String(err?.message || err);
      await setDoc(runRef, { status: "failed", error: message, updatedAt: serverTimestamp() }, { merge: true }).catch(() => {});
      setError(message);
    } finally {
      setIsGenerating(false);
    }
  };

  const deleteRun = async (run) => {
    if (!runsCollectionRef || !run?.id) return;
    const confirmed = window.confirm(`Delete generation run ${run.id} and all of its stored files? This cannot be undone.`);
    if (!confirmed) return;

    setDeletingId(run.id);
    setError("");
    setSuccess("");
    try {
      if (run.inputObjectPath) {
        await deleteObject(storageRef(storage, run.inputObjectPath)).catch((err) => {
          if (err?.code !== "storage/object-not-found") throw err;
        });
      }
      if (run.outputFolder) {
        const outputObjectFolder = String(run.outputFolder).replace(`${BUCKET_URI}/`, "");
        const outputRunId = outputObjectFolder.split("/").filter(Boolean).pop();
        if (outputRunId === run.id) {
          await deleteStorageFolder(storageRef(storage, outputObjectFolder));
        }
      }
      await deleteDoc(doc(runsCollectionRef, run.id));
      setSuccess("Generated-audio run deleted.");
    } catch (err) {
      setError(String(err?.message || "Could not delete the generated-audio run."));
    } finally {
      setDeletingId("");
    }
  };

  const regenerateRun = async (run) => {
    if (!runsCollectionRef || !run?.id) return;
    const steps = STEPS.filter((step) => selectedSteps.includes(step));
    if (!steps.length) {
      setError("Select at least one regeneration step.");
      return;
    }

    if (steps.includes("polish") && !steps.includes("generate") && !run.outlineUrl) {
      setError("This run does not have an outline to polish.");
      return;
    }
    if (steps.includes("audio") && !steps.includes("polish") && !run.transcriptUrl) {
      setError("This run does not have a full transcript to generate audio from.");
      return;
    }

    const outputFolder = String(run.outputFolder || "");
    const outputRunId = outputFolder.replace(`${BUCKET_URI}/`, "").split("/").filter(Boolean).pop();
    if (!outputFolder || outputRunId !== run.id) {
      setError("This older run has a mismatched output folder and cannot be regenerated safely.");
      return;
    }

    const statusFirestoreLocation = run.statusFirestoreLocation
      || `podcasts/${String(podcastId)}/audio_generation/${nodeIdStr}/${langCode}/${run.id}`;
    const inputLocation = String(run.inputLocation || "");
    if (!inputLocation) {
      setError("This run does not have a recorded input script location.");
      return;
    }

    setRegeneratingId(run.id);
    setIsDialogOpen(false);
    setRegenerationRun(null);
    setError("");
    setSuccess("");
    try {
      await setDoc(doc(runsCollectionRef, run.id), {
        status: "submitting",
        lastRequestedSteps: steps,
        updatedAt: serverTimestamp(),
      }, { merge: true });
      const result = await runPodcastPipeline({
        inputLocation,
        steps,
        outputFolder,
        statusFirestoreLocation,
      });
      await setDoc(doc(runsCollectionRef, run.id), {
        status: "submitted",
        lastRequestedSteps: steps,
        apiResponse: result || {},
        submittedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      }, { merge: true });
      setSuccess(`Audio regeneration started for run ${run.id}.`);
    } catch (err) {
      const message = String(err?.message || err);
      await setDoc(doc(runsCollectionRef, run.id), {
        status: "failed",
        error: message,
        lastRequestedSteps: steps,
        updatedAt: serverTimestamp(),
      }, { merge: true }).catch(() => {});
      setError(message);
    } finally {
      setRegeneratingId("");
    }
  };

  const canGenerate = !!runsCollectionRef && !!String(script || "").trim() && !isGenerating;

  return (
    <section style={cardStyle}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 16, color: "#020617" }}>Audio generated</div>
          <div style={{ color: "#64748b", fontSize: 14, lineHeight: 1.45, marginTop: 4, maxWidth: 590 }}>
            Generate an outline, polished transcript, and audio from the current English script.
          </div>
        </div>
        <button
          type="button"
          onClick={() => {
            setError("");
            setSuccess("");
            setSelectedSteps(STEPS);
            setRegenerationRun(null);
            setIsDialogOpen(true);
          }}
          disabled={!canGenerate}
          style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "9px 13px", borderRadius: 8, border: "1px solid #111827", background: canGenerate ? "#020617" : "#64748b", color: "#fff", fontWeight: 700, cursor: canGenerate ? "pointer" : "default" }}
        >
          <Sparkles size={16} />
          {isGenerating ? "Submitting…" : "Generate audio"}
        </button>
      </div>

      {isDialogOpen ? (
        <div
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && !isGenerating && !regeneratingId) {
              setIsDialogOpen(false);
              setRegenerationRun(null);
            }
          }}
          style={{ position: "fixed", inset: 0, zIndex: 2000, background: "rgba(15, 23, 42, 0.58)", display: "grid", placeItems: "center", padding: 20 }}
        >
          <div role="dialog" aria-modal="true" aria-labelledby="audio-generation-title" style={{ width: "min(560px, 100%)", background: "#fff", borderRadius: 12, boxShadow: "0 24px 60px rgba(15, 23, 42, 0.3)", padding: 22 }}>
            <div id="audio-generation-title" style={{ color: "#0f172a", fontSize: 20, fontWeight: 800 }}>{regenerationRun ? `Regenerate run ${regenerationRun.id}` : "Generate audio"}</div>
            <div style={{ color: "#64748b", fontSize: 14, marginTop: 5 }}>{regenerationRun ? "Choose which parts of this run to regenerate." : "Choose which processing steps to run."}</div>

            <div style={{ display: "grid", gap: 10, marginTop: 18 }}>
              {STEP_OPTIONS.map((option) => {
                const checked = selectedSteps.includes(option.id);
                return (
                  <label key={option.id} style={{ display: "flex", gap: 12, padding: 13, border: `1px solid ${checked ? "#2563eb" : "#dbe2ea"}`, borderRadius: 9, background: checked ? "#eff6ff" : "#fff", cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => setSelectedSteps((previous) => checked ? previous.filter((step) => step !== option.id) : [...previous, option.id])}
                      style={{ marginTop: 3 }}
                    />
                    <span>
                      <span style={{ display: "block", color: "#0f172a", fontWeight: 800 }}>{option.label}</span>
                      <span style={{ display: "block", color: "#64748b", fontSize: 13, lineHeight: 1.4, marginTop: 2 }}>{option.description}</span>
                    </span>
                  </label>
                );
              })}
            </div>

            {selectedSteps.includes("polish") && !selectedSteps.includes("generate") ? (
              <div style={{ color: "#92400e", background: "#fffbeb", borderRadius: 8, padding: 10, marginTop: 12, fontSize: 13 }}>Polish will reuse {regenerationRun ? "this run's existing outline" : "the newest available outline"}.</div>
            ) : null}
            {selectedSteps.includes("audio") && !selectedSteps.includes("polish") ? (
              <div style={{ color: "#92400e", background: "#fffbeb", borderRadius: 8, padding: 10, marginTop: 12, fontSize: 13 }}>Audio generation will reuse {regenerationRun ? "this run's existing full transcript" : "the newest available full transcript"}.</div>
            ) : null}
            {!selectedSteps.length ? <div style={{ color: "#b91c1c", fontWeight: 700, marginTop: 12, fontSize: 13 }}>Select at least one step.</div> : null}

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 9, marginTop: 20 }}>
              <button type="button" onClick={() => { setIsDialogOpen(false); setRegenerationRun(null); }} style={{ padding: "9px 14px", borderRadius: 8, border: "1px solid #cbd5e1", background: "#fff", color: "#334155", fontWeight: 700, cursor: "pointer" }}>Cancel</button>
              <button type="button" onClick={() => regenerationRun ? regenerateRun(regenerationRun) : generateAudio()} disabled={!selectedSteps.length || isGenerating || !!regeneratingId} style={{ padding: "9px 14px", borderRadius: 8, border: "1px solid #111827", background: selectedSteps.length ? "#020617" : "#64748b", color: "#fff", fontWeight: 700, cursor: selectedSteps.length ? "pointer" : "default" }}>{isGenerating || regeneratingId ? "Starting…" : regenerationRun ? "Start regeneration" : "Start generation"}</button>
            </div>
          </div>
        </div>
      ) : null}

      {!nodeIdStr ? <div style={{ marginTop: 12, color: "#92400e", fontWeight: 700 }}>Select a node before generating audio.</div> : null}
      {nodeIdStr && !String(script || "").trim() ? <div style={{ marginTop: 12, color: "#92400e", fontWeight: 700 }}>Add a script before generating audio.</div> : null}
      {error ? <div style={{ marginTop: 12, color: "crimson", fontWeight: 700 }}>{error}</div> : null}
      {success ? <div style={{ marginTop: 12, color: "#166534", fontWeight: 700 }}>{success}</div> : null}

      <div style={{ display: "grid", gap: 14, marginTop: 16 }}>
        {!runs.length ? <div style={{ padding: 26, border: "1px dashed #cbd5e1", borderRadius: 8, color: "#64748b", textAlign: "center" }}>No generated-audio runs for this node yet.</div> : null}
        {runs.map((run) => {
          const status = String(run.status || "submitted");
          const colors = statusColors[status] || statusColors.submitted;
          const audioUrl = run.mp3Url || run.wavUrl || "";
          const audioLabel = run.mp3Url ? "MP3 audio" : run.wavUrl ? "WAV audio" : "Audio";
          return (
            <div key={run.id} style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: 14, display: "grid", gap: 10 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                <div>
                  <strong style={{ color: "#0f172a" }}>Run {run.id}</strong>
                  <div style={{ color: "#64748b", fontSize: 12, marginTop: 2 }}>{formatDate(run.createdAt)}</div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ borderRadius: 999, padding: "4px 9px", background: colors.bg, color: colors.fg, fontSize: 12, fontWeight: 800 }}>{status.replaceAll("_", " ")}</span>
                  <button type="button" disabled={refreshingId === run.id} onClick={() => refreshRun(run)} style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "6px 9px", border: "1px solid #cbd5e1", borderRadius: 7, background: "#fff", cursor: "pointer" }}>
                    <RefreshCw size={14} /> {refreshingId === run.id ? "Refreshing…" : "Refresh outputs"}
                  </button>
                  <button type="button" disabled={regeneratingId === run.id} onClick={() => { setError(""); setSuccess(""); setSelectedSteps(STEPS); setRegenerationRun(run); setIsDialogOpen(true); }} style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "6px 9px", border: "1px solid #bfdbfe", borderRadius: 7, background: "#eff6ff", color: "#1d4ed8", cursor: regeneratingId === run.id ? "default" : "pointer" }} title="Choose which parts of this run to regenerate">
                    <RefreshCw size={14} /> {regeneratingId === run.id ? "Starting…" : "Regenerate"}
                  </button>
                  <button type="button" disabled={deletingId === run.id} onClick={() => deleteRun(run)} style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "6px 9px", border: "1px solid #fecaca", borderRadius: 7, background: "#fff", color: "#b91c1c", cursor: deletingId === run.id ? "default" : "pointer" }}>
                    <Trash2 size={14} /> {deletingId === run.id ? "Deleting…" : "Delete"}
                  </button>
                </div>
              </div>
              {run.error ? <div style={{ color: "crimson", fontWeight: 700 }}>{run.error}</div> : null}
              <OutputLink label="Outline" url={run.outlineUrl} icon={<FileText size={17} />} />
              <OutputLink label="Full transcript" url={run.transcriptUrl} icon={<FileText size={17} />} />
              <div style={{ padding: "10px 12px", border: "1px solid #e5e7eb", borderRadius: 8 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#0f172a", fontWeight: 700, marginBottom: audioUrl ? 8 : 0 }}><FileAudio size={17} />{audioLabel}</div>
                {audioUrl ? <audio controls src={audioUrl} style={{ width: "100%", height: 36 }} /> : <span style={{ color: "#94a3b8", fontSize: 13 }}>Not available</span>}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
